import {
    Box,
    Button,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Typography
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Api from "../services/Api";
import { processUrl } from "../services/EbayUrl";
import { MobileTooltip } from "./Tooltip";
import LinkOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from '@mui/icons-material/Check';

const pageLength = 8;
export const RecentlyViewed = ({ recentItems, isViewing, auth, preferredEbay, loadingUserData }) => {
    const recentItemsState = useMemo(() => {
        return recentItems.join(",");
    }, [recentItems])
    const [itemsWithData, setItemsWithData] = useState([]);
    const [page, setPage] = useState(0);
    const [pageLoaded, setPageLoaded] = useState(null);
    const [loading, setLoading] = useState(false);
    const pages = Math.ceil(recentItems.length / pageLength) - 1;
    const isLoading = loadingUserData || loading;

    useEffect(() => {
        if (!isViewing || recentItems.length === 0 || pageLoaded === page) return;
        const controller = new AbortController();
        const updateItems = async () => {
            const items = recentItems?.slice(page * pageLength, (page * pageLength) + pageLength);
            setLoading(true);
            try {
                const results = await Promise.allSettled(items.map(item => Api.fetchRecentItem(auth, item, controller)));
                if (controller.signal.aborted) return;
                const data = results.flatMap(result => result.status === "fulfilled" && result.value?.itemId ? [result.value] : []);
                setItemsWithData(prev => {
                    const loadedIds = new Set(prev.map(item => item.itemId));
                    return [...prev, ...data.filter(item => !loadedIds.has(item.itemId))];
                });
                setPageLoaded(page);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }
        updateItems();
        return () => controller.abort();
    }, [auth, recentItems, isViewing, page, pageLoaded]);

    useEffect(() => {
        setPage(0);
        setItemsWithData([]);
        setPageLoaded(null);
    }, [recentItemsState]);

    return <>{itemsWithData?.map((item, i) => <ListItemer key={item?.itemId ?? `recent-${i}`} item={item} preferredEbay={preferredEbay} />)}
        {isLoading && [{}, {}, {}, {}, {}, {}, {}, {}].map((_, index) =>
            <React.Fragment key={"recently-viewed-loading-" + index}>
                <ListItem sx={{ padding: 0 }}>
                    <ListItemButton sx={{ paddingLeft: "16px" }}>
                        <Skeleton variant="rectangular" width={100} height={100}
                            sx={{ borderRadius: "12px" }} />
                        <ListItemText sx={{ padding: "0 16px" }}>
                            <Skeleton />
                            <Skeleton width={"60%"} />
                        </ListItemText>
                        <Skeleton variant={"circular"} width={40} height={40} sx={{ margin: "0 16px" }} />
                    </ListItemButton>
                </ListItem>
                <Divider sx={{ marginLeft: "132px" }} />
            </React.Fragment>
        )
        }
        {
            pages > page &&
            <div style={{ display: "flex", width: "100%", justifyContent: "center", padding: "16px" }}>
                <Button disabled={isLoading} onClick={() => {
                    setPage(prev => prev + 1);
                }}>Load more</Button>
            </div>
        }
        {
            !isLoading && itemsWithData.length === 0 &&
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={'100%'}>
                <Typography sx={{ color: 'lightgray' }}>No Recently Viewed Listings</Typography>
            </Box>
        }
    </>
}


const ListItemer = ({ item, preferredEbay }) => {
    const [copied, setCopied] = useState(false);
    let itemUrl = preferredEbay ? processUrl(preferredEbay, item.itemWebUrl) : item.itemWebUrl;
    const timerCloseRef = useRef(null);
    const [anchorEl, setAnchorEl] = React.useState(null);

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(itemUrl);
        } catch {

        }
        setCopied(true);
        clearTimeout(timerCloseRef.current);
        timerCloseRef.current = setTimeout(() => {
            setAnchorEl(null);
        }, 2000);
    }


    const handleClick = (event) => {
        clearTimeout(timerCloseRef.current);
        setCopied(false);
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const opener = Boolean(anchorEl);
    if (!item.itemId) return;
    return (
        <React.Fragment key={"recently-viewed-" + item.itemId}>
            <ListItem sx={{ padding: 0 }} secondaryAction={
                <>
                    <MobileTooltip enterDelay={500} title={"More"}>
                        <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-haspopup="true"
                            onClick={handleClick}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </MobileTooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={opener}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={handleCopy}>
                            <ListItemIcon>
                                {!copied ? <LinkOutlinedIcon /> : <CheckIcon />}
                            </ListItemIcon>
                            {copied ? "Copied to clipboard" : "Copy eBay link"}</MenuItem>
                    </Menu>
                </>
            }>
                <a style={{ textDecoration: "none", color: "inherit", width: "100%" }} href={itemUrl} target="_blank"
                    rel="noreferrer noopener">
                    <ListItemButton disabled={!item.itemWebUrl} sx={{ paddingLeft: "16px" }}>
                        <img style={{ objectFit: "cover", height: 100, width: 100, minWidth: 100, borderRadius: 12 }}
                            src={item?.image?.imageUrl} alt={"ebay image"} />
                        <ListItemText sx={{ padding: "0 16px", wordWrap: "break-word", maxWidth: "60%" }}>
                            {item?.title}
                        </ListItemText>
                    </ListItemButton>
                </a>
            </ListItem>
            <Divider sx={{ marginLeft: "132px" }} />
        </React.Fragment>
    )
}